/**
 * 注释查找工具函数
 * 提供统一的注释查找和格式化功能
 */

import commentManager from '../services/commentManager';

export interface ParameterInfo {
  configFile: string;
  configSection: string;
  parameterKey: string;
}

export interface CommentResult {
  comment: string;
  description: string;
  found: boolean;
}

/**
 * 查找单个参数的注释
 */
export function findParameterComment(
  configFile: string, 
  configSection: string, 
  parameterKey: string
): CommentResult {
  const comment = commentManager.findComment(configFile, configSection, parameterKey);
  
  if (comment) {
    return {
      comment: comment.comment,
      description: comment.description,
      found: true
    };
  }

  return {
    comment: '',
    description: parameterKey, // 使用参数名作为默认描述
    found: false
  };
}

/**
 * 批量查找参数注释
 */
export function findParameterComments(parameters: ParameterInfo[]): { [key: string]: CommentResult } {
  const results: { [key: string]: CommentResult } = {};
  
  for (const param of parameters) {
    const key = `${param.configFile}_${param.configSection}_${param.parameterKey}`;
    results[key] = findParameterComment(param.configFile, param.configSection, param.parameterKey);
  }
  
  return results;
}

/**
 * 为配置数据添加注释信息
 */
export function enrichConfigWithComments(
  configData: any,
  configFile: string,
  configSection: string
): any {
  if (!configData || typeof configData !== 'object') {
    return configData;
  }

  const enrichedData: any = {};
  
  for (const [key, value] of Object.entries(configData)) {
    const commentResult = findParameterComment(configFile, configSection, key);
    
    enrichedData[key] = {
      key,
      value,
      type: typeof value === 'boolean' ? 'boolean' : 
            typeof value === 'number' ? 'number' : 'string',
      description: commentResult.description,
      comment: commentResult.comment,
      hasComment: commentResult.found
    };
  }
  
  return enrichedData;
}

/**
 * 为配置分组添加注释信息
 */
export function enrichConfigSectionsWithComments(
  configData: any,
  configFile: string
): any {
  if (!configData || typeof configData !== 'object') {
    return configData;
  }

  const enrichedSections: any = {};
  
  for (const [sectionKey, sectionData] of Object.entries(configData)) {
    if (sectionData && typeof sectionData === 'object') {
      enrichedSections[sectionKey] = enrichConfigWithComments(sectionData, configFile, sectionKey);
    } else {
      enrichedSections[sectionKey] = sectionData;
    }
  }
  
  return enrichedSections;
}

/**
 * 生成注释查找键（用于调试）
 */
export function generateCommentKeys(
  configFile: string,
  configSection: string,
  parameterKey: string
): string[] {
  return [
    `${configFile}_${configSection}_${parameterKey}`,
    `${configFile}_ServerSettings_${parameterKey}`,
    `${configFile}_/Script/ShooterGame.ShooterGameUserSettings_${parameterKey}`,
    `${configFile}_/Script/Engine.HUD_${parameterKey}`,
    `${configFile}_/Script/EngineSettings.GeneralProjectSettings_${parameterKey}`,
    `${configFile}_/Script/ShooterGame.ShooterGameMode_${parameterKey}`,
    `${configFile}_/Script/Engine.GameNetworkManager_${parameterKey}`,
    `${configFile}_/Script/Engine.GameSession_${parameterKey}`
  ];
}

/**
 * 检查注释管理器是否已初始化
 */
export function isCommentManagerReady(): boolean {
  return commentManager.isInitialized();
}

/**
 * 获取注释管理器统计信息
 */
export function getCommentManagerStats() {
  return commentManager.getCacheStats();
}

export default {
  findParameterComment,
  findParameterComments,
  enrichConfigWithComments,
  enrichConfigSectionsWithComments,
  generateCommentKeys,
  isCommentManagerReady,
  getCommentManagerStats
};










